// economy.ts — Client mirror of the server's authoritative sim.GameState wire
// contract (products/farstars/server/sim/sim.go). The server runs the simulation;
// the client renders these shapes by EXTRAPOLATING ship/orbit positions at the synced
// game clock between snapshots. Named SimState to avoid colliding with the focus/UI
// GameState in state/context.ts. Keep field names camelCase — one shared JSON contract.

/** A vessel. Kinematics are in the parent-star frame. posLocal/velLocal/heading are the
 *  integrated physical state of a powered leg; the client mirrors the server's flip-and-burn
 *  N-body integrator (shipPhysics.ts) to predict between snapshots. */
export interface Ship {
  id: string;
  owner: string;
  kind: string;
  systemId: string;
  posLocal: [number, number, number];
  velLocal: [number, number, number];
  /** Velocity carried across a hyperlane, applied to velLocal on the inbound leg. */
  entryVel: [number, number, number];
  /** Unit vector the engine thrusts along (the nose points). The client evolves this via the
   *  mirrored integrator between snapshots, so the flip-and-burn reorientation is visible. */
  heading: [number, number, number];
  /** Whether the engine fired on the last step — drives engine-glow brightness. */
  thrust: boolean;
  /** "transit" = powered in-system flight; "orbit" = parked at a planet; "hyperlane" = FTL. */
  phase: string;
  /** Current leg endpoints: planet ids, or gate ids "sysID>neighborID" (transit), the parked
   *  planet (orbit), or system ids (hyperlane). */
  originId: string;
  destId: string;
  departTime: number;
  arriveTime: number;
  /** Trade route endpoints (planet ids) the ship ferries between; TargetID is the one
   *  it is currently bound for. */
  routeA: string;
  routeB: string;
  targetId: string;
  pops: number;
  cargo: Record<string, number>;
  /** Empire ships only: remaining hyperlane hops, display name, hull, and firepower. */
  path?: string[];
  name?: string;
  hp?: number;
  maxHp?: number;
  attack?: number;
}

/** One player (human or AI) in a match. */
export interface Empire {
  id: string;
  name: string;
  color: string;
  ai: boolean;
  treasury: number;
  /** Net credits over the last game-day (tax − upkeep). */
  income: number;
  capital: string;
  alive: boolean;
  pops: number;
  worlds: number;
  ships: number;
}

/** A shipyard order; only the head of a world's queue progresses. */
export interface BuildOrder {
  kind: string;
  progress: number;
  days: number;
  cost: number;
}

/** A settled planet: who holds it ("" = independent), its people, defences, shipyard. */
export interface World {
  planetId: string;
  systemId: string;
  owner: string;
  pops: number;
  capacity: number;
  defense: number;
  maxDefense: number;
  queue: BuildOrder[] | null;
  besieged: boolean;
}

/** One line of the shared match log. */
export interface GameEvent {
  time: number;
  kind: string;
  text: string;
  empires: string[] | null;
  planetId?: string;
}

/** A player order (server/sim/empire.go Command). */
export type Command =
  | { type: 'build'; planetId: string; kind: string }
  | { type: 'cancelBuild'; planetId: string; index: number }
  | { type: 'move'; shipIds: string[]; targetId: string };

/** Build sheet of an empire ship kind — mirrors sim.ShipClasses. */
export interface ShipClass {
  kind: string;
  label: string;
  cost: number;
  days: number;
  hp: number;
  attack: number;
  upkeep: number;
  blurb: string;
}

export const SHIP_CLASSES: ShipClass[] = [
  { kind: 'frigate', label: 'Frigate', cost: 3000, days: 90, hp: 100, attack: 8, upkeep: 3, blurb: 'Cheap escort; fights and besieges.' },
  { kind: 'cruiser', label: 'Cruiser', cost: 8000, days: 180, hp: 320, attack: 22, upkeep: 8, blurb: 'Heavy warship for sieges.' },
  { kind: 'colony', label: 'Colony ship', cost: 4000, days: 180, hp: 60, attack: 0, upkeep: 2, blurb: 'Settles an empty world on arrival.' },
];

/** Minimum population for a world to run a shipyard (sim.MinYardPops). */
export const MIN_YARD_POPS = 1000;

/** Whether a ship belongs to an empire (vs the civilian trade fleet). */
export function isEmpireShip(s: Ship): boolean {
  return !!s.owner && s.owner !== 'system';
}

/** An empire's colour, or the civilian teal. */
export function ownerColor(sim: SimState | null, owner: string): string {
  return sim?.empires?.find((e) => e.id === owner)?.color ?? '#8fe9d0';
}

/** An empire's display name ("Independent" for unowned). */
export function ownerName(sim: SimState | null, owner: string): string {
  if (!owner) return 'Independent';
  return sim?.empires?.find((e) => e.id === owner)?.name ?? owner;
}

/** An economic population cohort. planetId xor shipId locates it. */
export interface PopGroup {
  id: string;
  planetId: string;
  shipId: string;
  job: string;
  count: number;
  foodReserve: number;
  money: number;
}

/** A planet's on-hand inventory of each good (the spot-market buffer). */
export interface Stockpile {
  planetId: string;
  goods: Record<string, number>;
}

/** A standing supply agreement: buyer buys good from seller at a locked price. */
export interface Contract {
  id: string;
  planetId: string;
  seller: string;
  buyer: string;
  good: string;
  rate: number;
  price: number;
  start: number;
}

/** A system's import-export trade hub: finances freight (buy surplus low, ship it,
 *  sell deficit high), capitalized by pops' invested disposable income. */
export interface Broker {
  id: string;
  systemId: string;
  money: number;
  profit: number;
  inventory: Record<string, number>;
}

// SimState is FOCUS-SCOPED: ships are global (all of them), but pops/stockpiles/contracts
// are only for the system the client has told the server it's viewing (the server
// simulates the whole galaxy). Empty until the client focuses a system.
export interface SimState {
  stateVersion: number;
  gameTime: number;
  speed: number;
  ships: Ship[];
  pops: PopGroup[];
  stockpiles: Stockpile[];
  contracts: Contract[];
  brokers: Broker[];
  /** The 4X layer (global, not focus-scoped). Empty in a sandbox galaxy. */
  empires: Empire[];
  worlds: World[];
  events: GameEvent[];
  victoryShare: number;
  timeLimit: number;
  over: boolean;
  winner: string;
}

/** The five tradeable goods, in display order (mirrors server/sim resource consts). */
export const RESOURCES = ['food', 'ore', 'alloy', 'fuel', 'goods'] as const;

// Constants mirrored from server/sim/sim.go — keep in lockstep with the Go values.
/** Food a pop (1e6 people) eats per game-day; used to render a reserve as days-of-food. */
export const FOOD_PER_POP_PER_DAY = 1.0;
/** In-system transfer leg duration (game-seconds). Lets the client loop a ship's
 *  course locally so motion stays smooth between server snapshots (matches ShipLegSeconds). */
export const SHIP_LEG_SECONDS = 30 * 86400;
